import React from 'react';
import {
  Box,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { IActivityMonitoring } from './activitymonitoring.models';

type Props = {
  activity?: IActivityMonitoring;
};

export default class ActivityInspector extends React.PureComponent<Props> {
  fmtMoney = (v?: number) => {
    const n = Number(v || 0);

    if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;

    return n.toLocaleString();
  };

  healthLabel = (hlt?: string) => {
    switch ((hlt || '').toUpperCase()) {
      case 'HIGH_VARIANCE': return '🔴 High Variance';
      case 'FINANCIAL_AHEAD': return '🟠 Financial Ahead';
      case 'PROGRESS_AHEAD': return '🟡 Progress Ahead';
      default: return '🟢 Aligned';
    }
  };

  render() {
    const { activity } = this.props;

    if (!activity) {
      return (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography color="text.secondary">
            Select an activity to inspect execution health.
          </Typography>
        </Paper>
      );
    }

    const implementation = Number(activity.apr || activity.rpr || 0);
    const financial = Number(activity.fpr || 0);
    const variance = Number(activity.var || 0);

    return (
      <Paper elevation={1} sx={{ p: 2, position: 'sticky', top: 16 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {activity.atc}
            </Typography>

            <Typography variant="h6">
              {activity.atn}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {activity.pdn} · {activity.cyn}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="small" label={this.healthLabel(activity.hlt)} />
            <Chip size="small" label={`Variance ${variance.toFixed(1)}%`} />
          </Stack>

          <Divider />

          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">Implementation</Typography>
              <Typography variant="caption">{implementation.toFixed(1)}%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, implementation))}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">Financial Disbursement</Typography>
              <Typography variant="caption">{financial.toFixed(1)}%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, financial))}
              sx={{ height: 8, borderRadius: 4 }}
            />

            <Typography variant="caption" color="text.secondary">
              Budget: {this.fmtMoney(activity.aca)} / {this.fmtMoney(activity.pam)}
            </Typography>
          </Box>

          <Divider />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="small" label={`Tasks ${activity.tct || 0}`} />
            <Chip size="small" label={`Completed ${activity.ctc || 0}`} />
            <Chip size="small" label={`In Progress ${activity.itc || 0}`} />
            <Chip size="small" label={`Not Started ${activity.ntc || 0}`} />
            <Chip size="small" label={`Waiting ${activity.wvc || 0}`} />
            <Chip size="small" label={`Evidence ${activity.evc || 0}`} />
            <Chip size="small" label={`Approvals ${activity.apc || 0}`} />
          </Stack>

          {(activity.exc || 0) > 0 && (
            <Box>
              <Typography variant="subtitle2">Needs Attention</Typography>
              <Typography variant="body2" color="text.secondary">
                This activity has {activity.exc} task(s) requiring review or corrective action.
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2">Last Update</Typography>
            <Typography variant="body2" color="text.secondary">
              {activity.lud || 'No progress update yet.'}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }
}